import { useMemo } from "react";
import type {
  Airline,
  BookedFlightItineraryWithDepartureTime,
} from "@b2bportal/air-booking-api";
import { useGetExperimentVariant } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PortalItineraryStatusEnum } from "@hopper-b2b/types";
import {
  getAirlineInfoFromBookedFlightItinerary,
  openExternalLink,
} from "@hopper-b2b/utilities";
import { Box, Typography, Link } from "@material-ui/core";
import { copyTextToClipboardPromise } from "../../../../../TripsList/components/ItineraryList/components/ItinerariesModal/components/ConfirmationModalContent/helpers";
import { parseProvidersFromFlagVariant, hasMatchingProvider } from "./helpers";
import "./styles.scss";

interface NoPostBookingBannerProps {
  flight: BookedFlightItineraryWithDepartureTime;
  airlineMap: { [key: string]: Airline | undefined };
  onOpenConfirmationModal?: () => void;
}

export const NoPostBookingBanner = ({
  flight,
  airlineMap,
  onOpenConfirmationModal,
}: NoPostBookingBannerProps) => {
  const { t } = useI18nContext();
  const flagVariant = useGetExperimentVariant("no-post-booking-providers");
  const providersWithoutPostBooking = useMemo(
    () => parseProvidersFromFlagVariant(flagVariant),
    [flagVariant]
  );

  const bannerData = useMemo(() => {
    // Only show banner for confirmed reservations
    if (flight.status !== PortalItineraryStatusEnum.Confirmed) {
      return null;
    }

    if (
      !hasMatchingProvider(
        flight.bookedItinerary.travelItinerary,
        providersWithoutPostBooking
      )
    ) {
      return null;
    }

    const segments = getAirlineInfoFromBookedFlightItinerary(flight);
    const firstSegment = segments[0];
    if (!firstSegment) return null;

    const airline = airlineMap[firstSegment.code];

    return {
      locator: firstSegment.locator || "",
      redirectUrl: airline?.webLinks?.manageBooking || "",
    };
  }, [flight, airlineMap, providersWithoutPostBooking]);

  const isMultiProvider =
    flight.bookedItinerary.travelItinerary?.TravelItinerary ===
    "MultiTravelItinerary";

  if (!bannerData) return null;

  const hasCtaAction = bannerData.locator || bannerData.redirectUrl;

  const handleCopyAndGo = async () => {
    if (isMultiProvider && onOpenConfirmationModal) {
      onOpenConfirmationModal();
      return;
    }
    if (!hasCtaAction) return;
    try {
      if (bannerData.locator) {
        await copyTextToClipboardPromise(bannerData.locator);
      }
    } catch {
      // Clipboard API may not be available in all contexts
    }
    if (bannerData.redirectUrl) {
      openExternalLink(bannerData.redirectUrl);
    }
  };

  return (
    <Box className="no-post-booking-banner">
      {/* Translation content is controlled (i18n files checked into repo) */}
      <Typography
        className="no-post-booking-banner-text"
        variant="body2"
        dangerouslySetInnerHTML={{
          __html: t("noPostBookingBanner.message"),
        }}
      />
      {hasCtaAction && (
        <Link
          className="no-post-booking-banner-cta"
          component="button"
          onClick={handleCopyAndGo}
          aria-label={t("noPostBookingBanner.cta")}
        >
          <Typography
            variant="body2"
            className="no-post-booking-banner-cta-text"
          >
            {t("noPostBookingBanner.cta")}
          </Typography>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className="no-post-booking-banner-cta-icon"
          >
            <path
              d="M4.66667 3.33334H12C12.1768 3.33334 12.3464 3.40357 12.4714 3.5286C12.5964 3.65362 12.6667 3.82319 12.6667 4V11.3333H11.3333V5.60948L4.4714 12.4714L3.52859 11.5286L10.3905 4.66667H4.66667V3.33334Z"
              fill="currentColor"
            />
          </svg>
        </Link>
      )}
    </Box>
  );
};
